import { MessageKey } from '../i18n/messages';
import { TodoProgressKind } from '../web-api-client';

/**
 * Bản sao ở phía trình duyệt của Domain/ValueObjects/TaskProgress.cs.
 *
 * Trạng thái task được lưu nguyên dạng chuỗi tiếng Việt trong JSON, nên giao diện phải đọc lại được
 * chuỗi đó để dựng ô chọn. Ba mẫu dưới đây phải khớp từng chữ với ba mẫu trong Domain — máy chủ mới
 * là bên quyết định, chỗ này chỉ để giao diện không hiển thị sai trước khi gửi đi.
 */
export const COMPLETED_TEXT = 'đã hoàn thành';
export const NOT_COMPLETED_TEXT = 'chưa hoàn thành';

const COMPLETED_PATTERN = /^đã\s+hoàn\s+thành$/i;
const NOT_COMPLETED_PATTERN = /^chưa\s+hoàn\s+thành$/i;
const PARTIAL_PATTERN = /^hoàn\s+thành\s+(\d{1,9})\s*\/\s*(\d{1,9})$/i;

export interface TaskProgress {
  kind: TodoProgressKind;
  /** Phần x trong "hoàn thành x/y"; chỉ có nghĩa khi kind là Partial. */
  done: number;
  /** Phần y trong "hoàn thành x/y". */
  total: number;
}

export function partialText(done: number, total: number): string {
  return `hoàn thành ${done}/${total}`;
}

export function parseTaskStatus(raw: string | undefined): TaskProgress {
  const text = (raw ?? '').trim();

  // "hoàn thành" là chuỗi con của "đã hoàn thành", nên thứ tự xét ở đây không đổi được.
  if (COMPLETED_PATTERN.test(text)) {
    return { kind: TodoProgressKind.Completed, done: 0, total: 0 };
  }

  if (NOT_COMPLETED_PATTERN.test(text)) {
    return { kind: TodoProgressKind.NotCompleted, done: 0, total: 0 };
  }

  const partial = PARTIAL_PATTERN.exec(text);

  if (partial) {
    return { kind: TodoProgressKind.Partial, done: Number(partial[1]), total: Number(partial[2]) };
  }

  // Chuỗi lạ được coi là chưa hoàn thành, giống hệt quy ước của Domain.
  return { kind: TodoProgressKind.NotCompleted, done: 0, total: 0 };
}

export function statusTextOf(progress: TaskProgress): string {
  switch (progress.kind) {
    case TodoProgressKind.Completed:
      return COMPLETED_TEXT;
    case TodoProgressKind.Partial:
      return partialText(progress.done, progress.total);
    default:
      return NOT_COMPLETED_TEXT;
  }
}

/** Chỉ giữ KHÓA dịch; câu chữ thật nằm trong i18n/messages.ts. */
export const PROGRESS_LABEL_KEY: Record<TodoProgressKind, MessageKey> = {
  [TodoProgressKind.Completed]: 'status.completed',
  [TodoProgressKind.Partial]: 'status.partial',
  [TodoProgressKind.NotCompleted]: 'status.notCompleted'
};

export const PROGRESS_TAG_CLASS: Record<TodoProgressKind, string> = {
  [TodoProgressKind.Completed]: 'tag tag-ok',
  [TodoProgressKind.Partial]: 'tag tag-warn',
  [TodoProgressKind.NotCompleted]: 'tag tag-idle'
};

/** Màu vạch dọc bên trái mỗi thẻ todo. */
export const PROGRESS_ACCENT_CLASS: Record<TodoProgressKind, string> = {
  [TodoProgressKind.Completed]: 'accent-ok',
  [TodoProgressKind.Partial]: 'accent-warn',
  [TodoProgressKind.NotCompleted]: 'accent-idle'
};
